/**
 * Фильтры нового поиска (docs/search-api.md):
 *   GET /api/organizations/search и GET /api/events/search.
 * Здесь — состояние фильтров по умолчанию, сборка query-параметров, проверка и описание
 * применённых фильтров (для «чипов» над списком).
 */

export const ONLINE_MODE_OPTIONS = [
  { value: 'all', label: 'Онлайн и офлайн' },
  { value: 'offline', label: 'Только офлайн' },
  { value: 'online', label: 'Только онлайн' },
];

export const ORGANIZATION_SOURCE_OPTIONS = [
  { value: 'admin', label: 'Создано админом' },
  { value: 'user', label: 'Создано пользователем' },
];

export const ORGANIZATION_SORT_OPTIONS = [
  { value: 'created', label: 'Сначала новые' },
  { value: 'rating', label: 'По рейтингу' },
];

export const EVENT_SORT_OPTIONS = [
  { value: 'created', label: 'Сначала новые' },
  { value: 'event_date', label: 'По ближайшей дате' },
];

export function getDefaultOrganizationFilters() {
  return {
    countryId: '',
    cityIds: [],
    onlineMode: 'all',
    categoryId: '',
    subCategoryIds: [],
    languageIds: [],
    source: '',
    createdFrom: '',
    createdTo: '',
    sort: 'created',
  };
}

export function getDefaultEventFilters() {
  return {
    countryId: '',
    cityIds: [],
    categoryIds: [],
    dateFrom: '',
    dateTo: '',
    includeExpired: true,
    languageIds: [],
    sources: [],
    createdFrom: '',
    createdTo: '',
    sort: 'created',
  };
}

/** Смещение часового пояса клиента от UTC в минутах (UTC+4 → 240), как требует API. */
export function getUtcOffsetMinutes() {
  return -new Date().getTimezoneOffset();
}

const joinIds = (ids) => (ids?.length ? ids.join(',') : undefined);

/** Параметры, общие для обоих методов поиска. */
function buildCommonParams({
  q, page, perPage, isActive,
}) {
  return {
    q: q?.trim() || undefined,
    page,
    per_page: perPage,
    is_active: isActive,
    count_all_totals: 'true',
    deep_populate: 'true',
    // Это админка: избранное и языки конкретного устройства в выдачу не подмешиваем.
    boost_favorites: 'false',
    use_account_languages: 'false',
    utc_offset_minutes: getUtcOffsetMinutes(),
  };
}

export function buildOrganizationSearchParams({
  q, page, perPage, isActive, filters,
}) {
  const isOnlineOnly = filters.onlineMode === 'online';

  return {
    ...buildCommonParams({
      q, page, perPage, isActive,
    }),
    should_add_views: 'false',
    sort_by_date: filters.sort === 'created' ? 'true' : 'false',
    online_mode: filters.onlineMode !== 'all' ? filters.onlineMode : undefined,
    // Для онлайн-предложений страна и города игнорируются — не отправляем их вовсе.
    country_id: !isOnlineOnly && filters.countryId ? filters.countryId : undefined,
    city_ids: !isOnlineOnly && filters.countryId ? joinIds(filters.cityIds) : undefined,
    category_id: filters.categoryId || undefined,
    sub_category_ids: filters.categoryId ? joinIds(filters.subCategoryIds) : undefined,
    language_ids: joinIds(filters.languageIds),
    source: filters.source || undefined,
    created_from: filters.createdFrom || undefined,
    created_to: filters.createdTo || undefined,
  };
}

export function buildEventSearchParams({
  q, page, perPage, isActive, filters,
}) {
  return {
    ...buildCommonParams({
      q, page, perPage, isActive,
    }),
    sort_by_date: filters.sort === 'event_date' ? 'true' : 'false',
    include_expired: filters.includeExpired ? 'true' : 'false',
    country_id: filters.countryId || undefined,
    city_ids: filters.countryId ? joinIds(filters.cityIds) : undefined,
    events_category_ids: joinIds(filters.categoryIds),
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    language_ids: joinIds(filters.languageIds),
    sources: joinIds(filters.sources),
    created_from: filters.createdFrom || undefined,
    created_to: filters.createdTo || undefined,
  };
}

/* ───────────────────────────── Проверка ───────────────────────────── */

const RANGE_ERROR = 'Дата «с» не может быть позже даты «по»';

function validateRange(from, to) {
  return from && to && from > to ? RANGE_ERROR : '';
}

/** Ошибки диапазонов дат (сервер ответил бы 400, поэтому ловим заранее). */
export function validateDateFilters({
  dateFrom, dateTo, createdFrom, createdTo,
}) {
  return {
    date: validateRange(dateFrom, dateTo),
    created: validateRange(createdFrom, createdTo),
  };
}

export function hasDateFiltersError(filters) {
  const errors = validateDateFilters(filters);
  return Boolean(errors.date || errors.created);
}

/* ───────────────────────── Описание для «чипов» ───────────────────────── */

function formatDate(value) {
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function formatRange(from, to) {
  if (from && to) return from === to ? formatDate(from) : `${formatDate(from)} — ${formatDate(to)}`;
  if (from) return `с ${formatDate(from)}`;
  return `по ${formatDate(to)}`;
}

function formatNames(ids, options) {
  const names = ids.map((id) => options.find((option) => option.value === id)?.label || '…');
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

const findLabel = (value, options) => options.find((option) => option.value === value)?.label || '…';

/**
 * Список применённых фильтров: [{ key, label, reset }], где reset — частичное
 * состояние, которое «снимает» этот фильтр.
 * lookups: { countries, cities, categories, subCategories, languages, eventCategories } —
 * массивы { value, label }.
 */
export function describeOrganizationFilters(filters, lookups) {
  const chips = [];
  const isOnlineOnly = filters.onlineMode === 'online';

  if (filters.onlineMode !== 'all') {
    chips.push({
      key: 'onlineMode',
      label: findLabel(filters.onlineMode, ONLINE_MODE_OPTIONS),
      reset: { onlineMode: 'all' },
    });
  }
  if (!isOnlineOnly && filters.countryId) {
    chips.push({
      key: 'country',
      label: `Страна: ${findLabel(filters.countryId, lookups.countries)}`,
      reset: { countryId: '', cityIds: [] },
    });
    if (filters.cityIds.length) {
      chips.push({
        key: 'cities',
        label: `Города: ${formatNames(filters.cityIds, lookups.cities)}`,
        reset: { cityIds: [] },
      });
    }
  }
  if (filters.categoryId) {
    chips.push({
      key: 'category',
      label: `Категория: ${findLabel(filters.categoryId, lookups.categories)}`,
      reset: { categoryId: '', subCategoryIds: [] },
    });
    if (filters.subCategoryIds.length) {
      chips.push({
        key: 'subCategories',
        label: `Подкатегории: ${formatNames(filters.subCategoryIds, lookups.subCategories)}`,
        reset: { subCategoryIds: [] },
      });
    }
  }
  if (filters.languageIds.length) {
    chips.push({
      key: 'languages',
      label: `Языки: ${formatNames(filters.languageIds, lookups.languages)}`,
      reset: { languageIds: [] },
    });
  }
  if (filters.source) {
    chips.push({
      key: 'source',
      label: findLabel(filters.source, ORGANIZATION_SOURCE_OPTIONS),
      reset: { source: '' },
    });
  }
  if (filters.createdFrom || filters.createdTo) {
    chips.push({
      key: 'created',
      label: `Создано: ${formatRange(filters.createdFrom, filters.createdTo)}`,
      reset: { createdFrom: '', createdTo: '' },
    });
  }

  return chips;
}

export function describeEventFilters(filters, lookups) {
  const chips = [];

  if (filters.countryId) {
    chips.push({
      key: 'country',
      label: `Страна: ${findLabel(filters.countryId, lookups.countries)}`,
      reset: { countryId: '', cityIds: [] },
    });
    if (filters.cityIds.length) {
      chips.push({
        key: 'cities',
        label: `Города: ${formatNames(filters.cityIds, lookups.cities)}`,
        reset: { cityIds: [] },
      });
    }
  }
  if (filters.categoryIds.length) {
    chips.push({
      key: 'categories',
      label: `Категории: ${formatNames(filters.categoryIds, lookups.eventCategories)}`,
      reset: { categoryIds: [] },
    });
  }
  if (filters.dateFrom || filters.dateTo) {
    chips.push({
      key: 'date',
      label: `Даты проведения: ${formatRange(filters.dateFrom, filters.dateTo)}`,
      reset: { dateFrom: '', dateTo: '' },
    });
  }
  if (!filters.includeExpired) {
    chips.push({
      key: 'expired',
      label: 'Без прошедших',
      reset: { includeExpired: true },
    });
  }
  if (filters.languageIds.length) {
    chips.push({
      key: 'languages',
      label: `Языки: ${formatNames(filters.languageIds, lookups.languages)}`,
      reset: { languageIds: [] },
    });
  }
  if (filters.sources.length) {
    chips.push({
      key: 'sources',
      label: `Источники: ${formatNames(filters.sources, lookups.sources)}`,
      reset: { sources: [] },
    });
  }
  if (filters.createdFrom || filters.createdTo) {
    chips.push({
      key: 'created',
      label: `Создано: ${formatRange(filters.createdFrom, filters.createdTo)}`,
      reset: { createdFrom: '', createdTo: '' },
    });
  }

  return chips;
}

/** Подсказка о том, как поиск понял текстовый запрос (поле `search` ответа). */
export function formatSearchInfo(search) {
  if (!search) return '';
  const parts = [];
  if (search.translated_query && search.translated_query !== search.query) {
    parts.push(`Ищем также: «${search.translated_query}»`);
  }
  if (search.tags?.length) {
    parts.push(`теги: ${search.tags.join(', ')}`);
  }
  return parts.join(' · ');
}
